export const CITATION_COLORS = {
  OFFICIAL_COMPANY_DATA: "#88B770",
  VISIBLE_ALPHA: "#D9AFE0",
  FACTSET: "#92CAD1",
  PRIVATE_MARKETS_DATA: "#708FB7",
  BROKER_RESEARCH: "#B492D1",
  INDUSTRY_RESEARCH: "#E0AFC8",
  WEB_SEARCH: "#FFD163",
  USER_FILES: "#AFE0CE",
  EXPERT_NETWORK_RESEARCH: "#68437A",
  CAPIQ: "#ED5C5F",
  CALCULATION: "#0E6B5D",
  ADIA: "#5B7BA5",
  DEFAULT: "#BFC3C9",
} as const;

export type CitationColor = (typeof CITATION_COLORS)[keyof typeof CITATION_COLORS];

const DATASOURCE_TO_COLOR: Record<string, CitationColor> = {
  FACTSET: CITATION_COLORS.FACTSET,
  FACTSET_FUNDAMENTALS: CITATION_COLORS.FACTSET,
  ESTIMATES: CITATION_COLORS.FACTSET,
  FACTSET_BUSINESS_GEO_SEGMENTS: CITATION_COLORS.FACTSET,
  FACTSET_PEOPLE: CITATION_COLORS.FACTSET,
  FACTSET_MA_DEALS: CITATION_COLORS.FACTSET,
  FACTSET_ETF: CITATION_COLORS.FACTSET,
  FACTSET_PRIVATE_COMPANY: CITATION_COLORS.FACTSET,
  FACTSET_OWNERSHIP: CITATION_COLORS.FACTSET,
  FACTSET_ECONOMIC_EVENTS: CITATION_COLORS.FACTSET,
  FACTSET_STREETACCOUNT_NEWS: CITATION_COLORS.FACTSET,
  FACTSET_INDEX_BENCHMARK: CITATION_COLORS.FACTSET,
  FACTSET_AMR: CITATION_COLORS.FACTSET,
  MARKET_DATA: CITATION_COLORS.FACTSET,
  "FactSet Fundamentals": CITATION_COLORS.FACTSET,
  "FactSet Consensus Estimates": CITATION_COLORS.FACTSET,
  "FactSet Segment-level data": CITATION_COLORS.FACTSET,
  "FactSet Global Share Price Trend data": CITATION_COLORS.FACTSET,
  "FactSet Earnings Surprise data": CITATION_COLORS.FACTSET,
  "FactSet Company Profiles": CITATION_COLORS.FACTSET,
  "FactSet StreetAccount News": CITATION_COLORS.FACTSET,
  "FactSet AMR": CITATION_COLORS.FACTSET,

  OFFICIAL_COMPANY_DATA: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  INVESTOR_RELATIONS_DOCUMENTS: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  SEC_FILINGS: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  COMPANY_WEBSITE: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  EARNING_AND_INVESTOR_CALLS: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  SEC: CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  "Company Website": CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  "Investor Relations Documents": CITATION_COLORS.OFFICIAL_COMPANY_DATA,
  Transcripts: CITATION_COLORS.OFFICIAL_COMPANY_DATA,

  BROKER_RESEARCH: CITATION_COLORS.BROKER_RESEARCH,
  MORNINGSTAR_EQUITY_RESEARCH: CITATION_COLORS.BROKER_RESEARCH,
  BLUEMATRIX_BROKER_RESEARCH: CITATION_COLORS.BROKER_RESEARCH,
  BlueMatrix: CITATION_COLORS.BROKER_RESEARCH,
  "Morningstar Reports": CITATION_COLORS.BROKER_RESEARCH,

  INDUSTRY_RESEARCH: CITATION_COLORS.INDUSTRY_RESEARCH,
  "Industry Research": CITATION_COLORS.INDUSTRY_RESEARCH,

  WEB_SEARCH: CITATION_COLORS.WEB_SEARCH,
  "The Web": CITATION_COLORS.WEB_SEARCH,

  VISIBLE_ALPHA: CITATION_COLORS.VISIBLE_ALPHA,
  "Visible Alpha": CITATION_COLORS.VISIBLE_ALPHA,
  PRIVATE_MARKETS_DATA: CITATION_COLORS.PRIVATE_MARKETS_DATA,
  PITCHBOOK_CRUNCHBASE: CITATION_COLORS.PRIVATE_MARKETS_DATA,
  PitchBook: CITATION_COLORS.PRIVATE_MARKETS_DATA,
  Crunchbase: CITATION_COLORS.PRIVATE_MARKETS_DATA,

  EXPERT_NETWORK_RESEARCH: CITATION_COLORS.EXPERT_NETWORK_RESEARCH,
  EXPERT_CALL_TRANSCRIPTS: CITATION_COLORS.EXPERT_NETWORK_RESEARCH,
  THIRD_BRIDGE_TRANSCRIPTS: CITATION_COLORS.EXPERT_NETWORK_RESEARCH,
  "Third Bridge Transcripts": CITATION_COLORS.EXPERT_NETWORK_RESEARCH,

  CAPIQ: CITATION_COLORS.CAPIQ,
  CAPIQ_ESTIMATES: CITATION_COLORS.CAPIQ,
  CAPIQ_FUNDAMENTALS: CITATION_COLORS.CAPIQ,
  "CapIQ Estimates": CITATION_COLORS.CAPIQ,
  "CapIQ Fundamentals": CITATION_COLORS.CAPIQ,

  USER_FILES: CITATION_COLORS.USER_FILES,
  "User Files": CITATION_COLORS.USER_FILES,

  CALCULATION: CITATION_COLORS.CALCULATION,
  calculation: CITATION_COLORS.CALCULATION,

  ADIA: CITATION_COLORS.ADIA,
  ADIA_FUND_DATA: CITATION_COLORS.ADIA,
  ADIA_MARKET_DATA: CITATION_COLORS.ADIA,
  ADIA_CRM_DOCUMENTS: CITATION_COLORS.ADIA,
  ADIA_INVESTMENT_PROPOSAL_DOCUMENTS: CITATION_COLORS.ADIA,
  ADIA_AUDITED_FINANCIAL_STATEMENTS: CITATION_COLORS.ADIA,
};

export function isCalculationType(
  id: string | null | undefined,
  dataSource: string | null | undefined,
  dataType?: string | null
): boolean {
  return (
    dataType === "calculation" ||
    dataSource === "CALCULATION" ||
    Boolean(id?.startsWith("CALC_"))
  );
}

export function getCitationColor(
  dataSource: string | undefined | null,
  options?: { id?: string | null; dataType?: string | null }
): CitationColor {
  if (isCalculationType(options?.id ?? null, dataSource ?? null, options?.dataType)) {
    return CITATION_COLORS.CALCULATION;
  }
  if (!dataSource) return CITATION_COLORS.DEFAULT;
  return DATASOURCE_TO_COLOR[dataSource] ?? CITATION_COLORS.DEFAULT;
}

export const SOURCE_CLIENT_LABELS: Record<string, string> = {
  get_web_data: "Web Search",
  get_transcripts_data: "Transcripts",
  get_sec_data: "SEC Filings",
  get_factset_data: "FactSet Fundamentals",
  get_estimates_data: "FactSet Estimates",
  get_industry_research_data: "Industry Research",
  get_company_website_data: "Company Website",
  get_investor_relations_data: "Investor Relations Documents",
  get_investor_relations_documents_data: "Investor Relations Documents",
  get_ir_documents_data: "Investor Relations Documents",
  get_reports_data: "Broker Research",
  get_global_price_trend_v2: "Market Data",
};

export function getCitationLabel(
  metadataDataSource: string | undefined,
  client: string | undefined
): string {
  if (metadataDataSource) return metadataDataSource;
  if (client && SOURCE_CLIENT_LABELS[client]) return SOURCE_CLIENT_LABELS[client];
  return "Other";
}
